import React, { useRef, useState, useEffect } from 'react'
import { usePageNavigation } from 'hooks/useNavigation'
import { uploadMedia } from 'utils/uploadMedia'
import {
  Platform,
  PostType,
  useCreateScheduledPostMutation,
  usePublishFacebookMutation,
  usePublishInstagramMutation,
  usePublishTikTokMutation,
} from 'graphql/generated/graphql'
import { Calendar, Clock, Send, Save, Eye, ArrowLeft, ArrowRight } from 'lucide-react'
import ModernSelectPlatform from './SelectPlatform'
import ModernPostContent from './PostContent'
import ModernSchedulePost from './SchedulePost'
import ModernConfirmPost from './ConfirmPost'

export interface IMediaAccounts {
  __typename?: 'MediaAccounts'
  id: number
  igUserId?: string | null
  accountName: string
  nickname?: string | null
  privacy_level_options?: string[] | null
  max_video_duration?: number | null
  comments_disabled?: boolean | null
  duet_disabled?: boolean | null
  stitch_disabled?: boolean | null
}

const steps = [
  { id: 1, name: 'Platform', icon: Calendar },
  { id: 2, name: 'Content', icon: Save },
  { id: 3, name: 'Schedule', icon: Clock },
  { id: 4, name: 'Confirm', icon: Eye },
]

export default function ModernPostScheduling(): JSX.Element {
  const [activeStep, setActiveStep] = useState(0)
  const [selectedPlatform, setSelectedPlatform] = useState('Instagram')
  const [imageFiles, setImageFiles] = useState<any[]>([])
  const [videoFiles, setVideoFiles] = useState<any[]>([])
  const captionRef = useRef<string>('')
  const [pickerValue, setPickerValue] = useState<any>()
  const [switchValue, setSwitchValue] = useState(false)
  const [mediaLoading, setMediaLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const [profileValue, setProfileValue] = useState<IMediaAccounts | undefined>()
  const [tiktokAdditionalData, setTiktokAdditionalData] = useState({
    privacy_level: '',
    allow_comments: false,
    allow_duet: false,
    allow_stitch: false,
    brand_content: false,
    brand_organic: false,
  })
  const [postTypeValue, setPostTypeValue] = useState('image')
  
  const [publishTikTok] = usePublishTikTokMutation()
  const [publishFacebook, { loading: facebookLoading }] = usePublishFacebookMutation()
  const [publishInstagram, { loading }] = usePublishInstagramMutation()
  const [schedulePost, { loading: scheduleLoading }] = useCreateScheduledPostMutation()
  const { navigateToDashboard } = usePageNavigation()
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [activeStep])

  const handleOnNext = async (): Promise<void> => {
    if (activeStep === 0) {
      setImageFiles([])
      setVideoFiles([])
    }
    if (activeStep === steps.length - 1) {
      // FINAL STEP - Publishing logic
      if (selectedPlatform === 'Facebook') {
        const uploadedKeys: string[] = []
        setMediaLoading(true)
        if (postTypeValue === 'image') {
          const imageName = imageFiles[0].name.replaceAll(' ', '') as string
          const key = `${imageName}-${new Date().getTime()}-imageFile`
          const uploaded = await uploadMedia(key, imageFiles[0])
          if (uploaded) {
            uploadedKeys.push(
              `https://nalapod-post-scheduling.s3.us-west-2.amazonaws.com/${key}`
            )
          } else {
            setErrorMessage('Failed to upload media.')
            return
          }
        } else {
          const videoName = videoFiles[0].name.replaceAll(' ', '') as string
          const key = `${videoName}-${new Date().getTime()}-videoFile`
          const uploaded = await uploadMedia(key, videoFiles[0])
          if (uploaded) {
            uploadedKeys.push(
              `https://nalapod-post-scheduling.s3.us-west-2.amazonaws.com/${key}`
            )
          } else {
            setErrorMessage('Failed to upload media.')
            return
          }
        }
        setMediaLoading(false)
        const published = await publishFacebook({
          variables: {
            accountId: profileValue?.id ?? 1,
            caption: captionRef.current,
            mediaURL: uploadedKeys[0],
            published: !switchValue,
            scheduledDate: new Date(pickerValue._d).getTime().toString() ?? '',
            isVideo: postTypeValue === 'video',
          },
        })

        if (published?.data?.publishFacebook) {
          navigateToDashboard()
        } else {
          setErrorMessage('Failed publishing media.')
        }
      }
      if (selectedPlatform === 'Instagram') {
        const isCarousel =
          imageFiles.length > 1 ||
          (imageFiles.length >= 1 && videoFiles.length >= 1) ||
          videoFiles.length > 1
        const isVideo = videoFiles.length === 1 && imageFiles.length === 0

        const uploadedKeys: string[] = []
        if (isCarousel) {
          setMediaLoading(true)
          for (let i = 0; i < videoFiles.length; i++) {
            const key = `${videoFiles[0]?.name as string}-${new Date().getTime()}-videoFile`
            const uploaded = await uploadMedia(key, videoFiles[i])
            if (uploaded) {
              uploadedKeys.push(
                `https://nalapod-post-scheduling.s3.us-west-2.amazonaws.com/${key}`
              )
            } else {
              setErrorMessage('Failed to upload media.')
              return
            }
          }
          for (let j = 0; j < imageFiles.length; j++) {
            const key = `${imageFiles[j]?.name as string}-${new Date().getTime()}-imageFile`
            const uploaded = await uploadMedia(key, imageFiles[j])
            if (uploaded) {
              uploadedKeys.push(
                `https://nalapod-post-scheduling.s3.us-west-2.amazonaws.com/${key}`
              )
            } else {
              setErrorMessage('Failed to upload media.')
              return
            }
          }
          setMediaLoading(false)
          if (switchValue) {
            const scheduled = await schedulePost({
              variables: {
                imageMediaURL: uploadedKeys.filter((x) => x.includes('-imageFile')),
                platform: Platform.Instagram,
                postDate: new Date(pickerValue._d).getTime().toString() ?? '',
                postType: PostType.Carousel,
                videoMediaURL: uploadedKeys.filter((x) => x.includes('-videoFile')),
                caption: captionRef.current,
                mediaAccountId: profileValue?.id ?? 1,
              },
            })
            if (scheduled.data?.schedulePost) {
              navigateToDashboard()
            } else {
              setErrorMessage('Failed scheduling media.')
            }
          } else {
            const published = await publishInstagram({
              variables: {
                accountId: profileValue?.id ?? 1,
                mediaURLs: uploadedKeys,
                postType: PostType.Carousel,
                caption: captionRef.current,
              },
            })
            if (published.data?.publishInstagram) {
              navigateToDashboard()
            } else {
              setErrorMessage('Failed publishing media.')
            }
          }
        } else {
          const key = `${
            isVideo ? (videoFiles[0]?.name as string) : (imageFiles[0]?.name as string)
          }-${new Date().getTime()}`
          setMediaLoading(true)
          const uploaded = await uploadMedia(key, isVideo ? videoFiles[0] : imageFiles[0])
          setMediaLoading(false)
          if (uploaded) {
            uploadedKeys.push(
              `https://nalapod-post-scheduling.s3.us-west-2.amazonaws.com/${key}`
            )
          } else {
            setErrorMessage('Failed to upload media.')
            return
          }
          if (switchValue) {
            const scheduled = await schedulePost({
              variables: {
                imageMediaURL: isVideo ? [] : uploadedKeys,
                platform: Platform.Instagram,
                postDate: new Date(pickerValue._d).getTime().toString() ?? '',
                postType: isVideo ? PostType.Video : PostType.Image,
                videoMediaURL: isVideo ? uploadedKeys : [],
                caption: captionRef.current,
                mediaAccountId: profileValue?.id ?? 1,
              },
            })
            if (scheduled.data?.schedulePost) {
              navigateToDashboard()
            } else {
              setErrorMessage('Failed publishing media.')
            }
          } else {
            const published = await publishInstagram({
              variables: {
                accountId: profileValue?.id ?? 1,
                mediaURLs: uploadedKeys,
                postType: isVideo ? PostType.Video : PostType.Image,
                caption: captionRef.current,
              },
            })
            if (published.data?.publishInstagram) {
              navigateToDashboard()
            } else {
              setErrorMessage('Failed publishing media.')
            }
          }
        }
      }
      if (selectedPlatform === 'TikTok') {
        const key = `tiktok-${videoFiles[0]?.name as string}`
        setMediaLoading(true)
        const uploaded = await uploadMedia(key, videoFiles[0])
        setMediaLoading(false)
        if (!uploaded) {
          setErrorMessage('Failed to upload media.')
          return
        }
        if (switchValue) {
          const scheduled = await schedulePost({
            variables: {
              imageMediaURL: [],
              platform: Platform.TikTok,
              postDate: new Date(pickerValue._d).getTime().toString() ?? '',
              postType: PostType.Video,
              videoMediaURL: [
                `https://nalapod-post-scheduling.s3.us-west-2.amazonaws.com/${key}`,
              ],
              caption: captionRef.current,
              mediaAccountId: profileValue?.id ?? 1,
              privacy_level: tiktokAdditionalData.privacy_level,
              allow_comments: tiktokAdditionalData.allow_comments,
              allow_duet: tiktokAdditionalData.allow_duet,
              allow_stitch: tiktokAdditionalData.allow_stitch,
              brand_content: tiktokAdditionalData.brand_content,
              brand_organic: tiktokAdditionalData.brand_organic,
            },
          })
          if (scheduled.data?.schedulePost) {
            navigateToDashboard()
          } else {
            setErrorMessage('Failed publishing media.')
          }
        } else {
          const published = await publishTikTok({
            variables: {
              mediaURL: `https://nalapod-post-scheduling.s3.us-west-2.amazonaws.com/${key}`,
              accountId: profileValue?.id ?? 1,
              caption: captionRef.current,
              privacy_level: tiktokAdditionalData.privacy_level,
              allow_comments: tiktokAdditionalData.allow_comments,
              allow_duet: tiktokAdditionalData.allow_duet,
              allow_stitch: tiktokAdditionalData.allow_stitch,
              brand_content: tiktokAdditionalData.brand_content,
              brand_organic: tiktokAdditionalData.brand_organic,
            },
          })
          if (published.data?.publishTikTok) {
            navigateToDashboard()
          } else {
            setErrorMessage('Failed publishing media.')
          }
        }
      }
    } else {
      setActiveStep(activeStep + 1)
    }
  }

  const checkDisabled = (): boolean => {
    if (activeStep === 0) {
      return false
    } else if (activeStep === 1) {
      if (selectedPlatform === Platform.Facebook) {
        if (
          (postTypeValue === 'image' ? imageFiles.length : videoFiles.length) &&
          profileValue
        ) {
          return false
        } else {
          return true
        }
      }
      if (selectedPlatform === Platform.Instagram) {
        if ((imageFiles.length || videoFiles.length) && profileValue) {
          return false
        } else {
          return true
        }
      } else if (selectedPlatform === Platform.TikTok) {
        if (videoFiles.length && profileValue && tiktokAdditionalData.privacy_level) {
          return false
        } else {
          return true
        }
      } else {
        return true
      }
    } else if (activeStep === 2) {
      if (switchValue) {
        return !pickerValue
      } else {
        return false
      }
    } else {
      return false
    }
  }

  const isLoading = mediaLoading || loading || scheduleLoading || facebookLoading

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #581c87 50%, #0f172a 100%)',
        padding: '40px 20px',
      }}
    >
      <div
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          background: 'rgba(30, 41, 59, 0.5)',
          backdropFilter: 'blur(24px)',
          borderRadius: '24px',
          border: '1px solid rgba(168, 85, 247, 0.2)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          overflow: 'hidden',
        }}
      >
        {/* Progress Steps */}
        <div
          style={{
            padding: '32px 40px',
            borderBottom: '1px solid rgba(168, 85, 247, 0.2)',
            background: 'rgba(15, 23, 42, 0.6)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              position: 'relative',
            }}
          >
            {/* Progress Line */}
            <div
              style={{
                position: 'absolute',
                top: '24px',
                left: '24px',
                right: '24px',
                height: '2px',
                background: 'rgba(168, 85, 247, 0.2)',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${(activeStep / (steps.length - 1)) * 100}%`,
                  background: 'linear-gradient(90deg, #a855f7 0%, #ec4899 100%)',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>

            {steps.map((step, index) => {
              const StepIcon = step.icon
              const isActive = index === activeStep
              const isCompleted = index < activeStep
              return (
                <div
                  key={step.id}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: isActive || isCompleted
                        ? 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)'
                        : 'rgba(30, 41, 59, 0.8)',
                      border: `2px solid ${
                        isActive || isCompleted ? 'transparent' : 'rgba(168, 85, 247, 0.3)'
                      }`,
                      boxShadow: isActive
                        ? '0 0 20px rgba(168, 85, 247, 0.6)'
                        : 'none',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <StepIcon
                      size={24}
                      style={{
                        color: isActive || isCompleted
                          ? '#fff'
                          : 'rgba(255, 255, 255, 0.5)',
                      }}
                    />
                  </div>
                  <span
                    style={{
                      marginTop: '12px',
                      fontSize: '14px',
                      fontWeight: isActive ? 600 : 400,
                      color: isActive
                        ? '#fff'
                        : isCompleted
                        ? 'rgba(255, 255, 255, 0.8)'
                        : 'rgba(255, 255, 255, 0.5)',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {step.name}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Content Area */}
        <div style={{ padding: '40px', minHeight: '500px' }}>
          {activeStep === 0 && (
            <ModernSelectPlatform
              selectedPlatform={selectedPlatform}
              setSelectedPlatform={setSelectedPlatform}
            />
          )}
          {activeStep === 1 && (
            <ModernPostContent
              imageFiles={imageFiles}
              setImageFiles={setImageFiles}
              videoFiles={videoFiles}
              setVideoFiles={setVideoFiles}
              setProfileValue={setProfileValue}
              selectedPlatform={selectedPlatform}
              profileValue={profileValue}
              captionRef={captionRef}
              tiktokAdditionalData={tiktokAdditionalData}
              setTiktokAdditionalData={setTiktokAdditionalData}
              postTypeValue={postTypeValue}
              setPostTypeValue={setPostTypeValue}
            />
          )}
          {activeStep === 2 && (
            <ModernSchedulePost
              pickerValue={pickerValue}
              setPickerValue={setPickerValue}
              switchValue={switchValue}
              setSwitchValue={setSwitchValue}
            />
          )}
          {activeStep === 3 && (
            <ModernConfirmPost
              imageFiles={imageFiles}
              videoFiles={videoFiles}
              captionValue={captionRef.current}
              pickerValue={pickerValue}
              switchValue={switchValue}
              errorMessage={errorMessage}
              profileValue={profileValue}
              platform={selectedPlatform}
              tiktokAdditionalData={tiktokAdditionalData}
            />
          )}
        </div>

        {/* Footer Buttons */}
        <div
          style={{
            padding: '24px 40px',
            borderTop: '1px solid rgba(168, 85, 247, 0.2)',
            background: 'rgba(15, 23, 42, 0.6)',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          {activeStep !== 0 ? (
            <button
              onClick={() => setActiveStep(activeStep - 1)}
              disabled={isLoading}
              style={{
                padding: '12px 24px',
                borderRadius: '12px',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                background: 'rgba(30, 41, 59, 0.5)',
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '15px',
                fontWeight: 500,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: isLoading ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.background = 'rgba(168, 85, 247, 0.1)'
                  e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.5)'
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(30, 41, 59, 0.5)'
                e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.3)'
              }}
            >
              <ArrowLeft size={18} />
              Back
            </button>
          ) : (
            <div />
          )}

          <button
            onClick={() => { void handleOnNext() }}
            disabled={checkDisabled() || isLoading}
            style={{
              padding: '12px 32px',
              borderRadius: '12px',
              border: 'none',
              background: checkDisabled() || isLoading
                ? 'rgba(168, 85, 247, 0.2)'
                : 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              color: '#fff',
              fontSize: '15px',
              fontWeight: 600,
              cursor: checkDisabled() || isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: checkDisabled() || isLoading
                ? 'none'
                : '0 4px 15px rgba(168, 85, 247, 0.4)',
            }}
            onMouseEnter={(e) => {
              if (!checkDisabled() && !isLoading) {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(168, 85, 247, 0.6)'
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = checkDisabled() || isLoading
                ? 'none'
                : '0 4px 15px rgba(168, 85, 247, 0.4)'
            }}
          >
            {isLoading ? (
              <>
                <div
                  style={{
                    width: '18px',
                    height: '18px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderTopColor: '#fff',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                  }}
                />
                Processing...
              </>
            ) : activeStep === steps.length - 1 ? (
              <>
                <Send size={18} />
                Publish Post
              </>
            ) : (
              <>
                Next
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Loading Spinner Animation */}
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  )
}